import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Edit } from 'lucide-react'
import { getCustomer } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import StatusBadge from '../components/ui/StatusBadge'

// Mirrors NOT_OWED in backend-books/src/services/customerInvoiceList.js, which is
// what the Outstanding total excludes.
const NOT_OWED = ['CANCELLED', 'DRAFT']

export default function CustomerDetail() {
  const { id } = useParams()
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    getCustomer(id).then((res) => setCustomer(res.data)).catch(() => {}).finally(() => setLoading(false))
  }, [id])

  // Hooks must run on every render, so this sits above the early returns and is
  // given an empty list until the fetch lands.
  const invoices = customer?.invoices || []
  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(invoices, {
    defaultSort: 'date',
    defaultDir: 'desc',
    pageSize: 25,
    storageKey: 'customer-invoices',
  })

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 w-48 bg-gray-200 rounded" /><div className="h-40 bg-gray-100 rounded" /></div>
  if (!customer) return <p className="text-gray-500">Customer not found.</p>

  const c = customer

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/customers" className="p-1.5 text-gray-400 hover:text-gray-600"><ArrowLeft className="h-5 w-5" /></Link>
        <h1 className="text-2xl font-semibold text-gray-900">{c.name}</h1>
        <Link to={`/customers/${id}/edit`} className="btn-secondary btn-sm flex items-center gap-1"><Edit className="h-3.5 w-3.5" /> Edit</Link>
      </div>

      <div className="card grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        <div><span className="text-gray-500 block">Type</span>{c.type}</div>
        <div><span className="text-gray-500 block">GSTIN</span><span className="font-mono">{c.gstin || '—'}</span></div>
        <div><span className="text-gray-500 block">PAN</span><span className="font-mono">{c.pan || '—'}</span></div>
        <div><span className="text-gray-500 block">Email</span>{c.email || '—'}</div>
        <div><span className="text-gray-500 block">Phone</span>{c.phone || '—'}</div>
        <div><span className="text-gray-500 block">State</span>{c.gstStateCode || '—'}</div>
        {c.billingAddress && <div className="col-span-full"><span className="text-gray-500 block">Billing Address</span>{c.billingAddress}</div>}
        {/* The API returns totalOutstanding; this read `outstandingBalance`, which
            the response has never carried, so the figure never rendered at all.
            It matters here because it is computed over EVERY invoice — it is the
            number the list below has to reconcile against. */}
        {c.totalOutstanding !== undefined && (
          <div><span className="text-gray-500 block">Outstanding</span><span className="font-semibold text-red-600">{formatCurrency(c.totalOutstanding)}</span></div>
        )}
      </div>

      {invoices.length > 0 && (
        <div className="card p-0">
          <div className="flex items-baseline justify-between px-4 pt-4 pb-3">
            <h2 className="text-lg font-medium text-gray-900">Invoices</h2>
            <span className="text-sm text-gray-500">{total}</span>
          </div>

          {/* Only ever shown when the response actually hit the cap. Silence here
              is what made the old 20-row limit invisible. */}
          {c.invoicesTruncated && (
            <p className="mx-4 mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Showing the most recent {c.invoiceListCap} invoices of {c._count?.invoices ?? 'more'}.
              Use the Invoices page to see the rest.
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                  <SortHeader label="Invoice" sortKey="number" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Amount" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                  <SortHeader label="Balance" sortKey="balanceDue" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                  <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {paged.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0 hover:bg-gray-50/70 transition-colors">
                    <td className="px-4 py-2.5">
                      <Link to={`/invoices/${inv.id}`} className="text-brand-600 hover:underline font-medium">{inv.number || 'DRAFT'}</Link>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{formatDate(inv.date)}</td>
                    <td className="px-4 py-2.5 text-right">{formatCurrency(inv.total, inv.currency)}</td>
                    {/* A cancelled or draft invoice owes nothing, whatever balanceDue
                        still holds — cancelling never zeroes it. Five cancelled invoices
                        on the live book still read as owing 76,155 across four customers,
                        and the Outstanding figure above excludes them. Printing the stale
                        number here would make the two contradict each other on the one
                        page whose job is to reconcile them. */}
                    <td className={`px-4 py-2.5 text-right ${inv.status === 'OVERDUE' && inv.balanceDue > 0 ? 'text-red-600 font-medium' : ''}`}>
                      {NOT_OWED.includes(inv.status)
                        ? <span className="text-gray-400">—</span>
                        : formatCurrency(inv.balanceDue, inv.currency)}
                    </td>
                    <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
